import Axios from 'axios';
import { getRecaptchaKey } from '../../helper';
import { aBffError, BffError } from './bffError';
import { aQueryString, ejecutarEnBff, urlDeBff } from '../bffHttp';
import { SseParser, parseSseData } from './sseParser';

/**
 * Cliente del módulo auto/moto del BFF. Espeja el estilo de `bffClient.ts`:
 * base `config.bff_url`, recaptcha v3 FRESCO por request en el header
 * `g-recaptcha-response` (nunca en el body).
 *
 * Suma sobre aquel:
 *  - GET con query params,
 *  - requests autenticadas con el JWT de cotización (`Authorization: Bearer`),
 *  - SSE (`POST` + ReadableStream) para precios,
 *  - normalización de cualquier falla a `BffError`.
 */

const RECAPTCHA_HEADER = 'g-recaptcha-response';

/** Acciones de recaptcha que el BFF valida por endpoint (contrato §2). */
export type AccionRecaptcha = 'contacto' | 'patente';

const mintRecaptchaToken = async (action: AccionRecaptcha): Promise<string> => {
  const key = await getRecaptchaKey();
  return new Promise<string>((resolve, reject) => {
    try {
      grecaptcha.ready(() => {
        grecaptcha
          .execute(key!, { action })
          .then((token: string) => resolve(token), reject);
      });
    } catch (error) {
      reject(error);
    }
  });
};

/** Movidos a `bff/bffHttp.ts` (compartidos con landings). */
export { aQueryString };
const urlDe = urlDeBff;
const ejecutar = ejecutarEnBff;

/** GET anónimo (catálogos, parámetros, lookups). */
export const getFromBff = async <T>(
  path: string,
  params?: Record<string, string | number | boolean | undefined | null>
): Promise<T> =>
  ejecutar<T>({
    method: 'GET',
    url: (await urlDe(path)) + aQueryString(params),
  });

/** POST anónimo con recaptcha fresco en el header (contacto, patente). */
export const postToBffWithRecaptcha = async <TReq, TRes>(
  path: string,
  body: TReq,
  action: AccionRecaptcha
): Promise<TRes> => {
  const captcha = await mintRecaptchaToken(action);
  return ejecutar<TRes>({
    method: 'POST',
    url: await urlDe(path),
    data: body,
    headers: { [RECAPTCHA_HEADER]: captcha },
  });
};

/** POST con el JWT de cotización (precios, selección, pasos, emisión). */
export const postToBffWithQuoteToken = async <TReq, TRes>(
  path: string,
  body: TReq,
  token: string,
  headersExtra?: Record<string, string>
): Promise<TRes> =>
  ejecutar<TRes>({
    method: 'POST',
    url: await urlDe(path),
    data: body,
    headers: { Authorization: `Bearer ${token}`, ...(headersExtra || {}) },
  });

/** GET con el JWT de cotización (snapshot, estado de póliza). */
export const getWithQuoteToken = async <T>(
  path: string,
  token: string,
  params?: Record<string, string | number | boolean | undefined | null>
): Promise<T> =>
  ejecutar<T>({
    method: 'GET',
    url: (await urlDe(path)) + aQueryString(params),
    headers: { Authorization: `Bearer ${token}` },
  });

/** GET de un documento con el JWT de cotización; devuelve el PDF como Blob. */
export const getBlobWithQuoteToken = async (
  path: string,
  token: string
): Promise<Blob> => {
  try {
    const respuesta = await Axios.get<Blob>(await urlDe(path), {
      headers: { Authorization: `Bearer ${token}` },
      responseType: 'blob',
    });
    return respuesta.data;
  } catch (error) {
    // Con responseType blob el cuerpo del error también viene como Blob: se
    // lee como texto para poder parsear el envelope `{error:{code,...}}`.
    const respuesta = (error as { response?: { status?: number; data?: unknown } })
      ?.response;
    const cuerpo = respuesta?.data;
    if (cuerpo instanceof Blob) {
      const texto = await cuerpo.text().catch(() => '');
      let parseado: unknown;
      try {
        parseado = JSON.parse(texto);
      } catch {
        parseado = undefined;
      }
      throw aBffError({ response: { status: respuesta?.status, data: parseado } });
    }
    throw aBffError(error);
  }
};

/* ───────────────── SSE ───────────────── */

export interface SseHandlers<TStarted, TResult, TError, TDone> {
  onStarted?: (data: TStarted) => void;
  onResult?: (data: TResult) => void;
  onError?: (data: TError) => void;
  onPing?: () => void;
  onDone?: (data: TDone) => void;
  /** Falla del transporte (HTTP != 200, corte de red, JSON roto). */
  onFailure?: (error: BffError) => void;
}

export interface SseConexion {
  /** Corta el stream. Idempotente. */
  cancelar: () => void;
  /** Resuelve cuando el stream termina (done, corte o cancelación). */
  terminado: Promise<void>;
}

/**
 * Abre un stream SSE contra el BFF con `POST` + JWT de cotización.
 *
 * No se usa `EventSource`: solo soporta GET y no permite mandar el header
 * `Authorization`. Se usa `fetch` + `ReadableStream`, con un `SseParser` que
 * tolera eventos partidos entre chunks.
 */
export const openQuoteSse = <
  TStarted = unknown,
  TResult = unknown,
  TError = unknown,
  TDone = unknown
>(
  path: string,
  body: unknown,
  token: string,
  handlers: SseHandlers<TStarted, TResult, TError, TDone>
): SseConexion => {
  const abort = new AbortController();
  let cerrado = false;

  const cancelar = () => {
    if (cerrado) return;
    cerrado = true;
    abort.abort();
  };

  const terminado = (async () => {
    try {
      const respuesta = await fetch(await urlDe(path), {
        method: 'POST',
        headers: {
          'content-type': 'application/json',
          accept: 'text/event-stream',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(body ?? {}),
        signal: abort.signal,
      });

      if (!respuesta.ok) {
        const cuerpo = await respuesta.json().catch(() => undefined);
        handlers.onFailure?.(
          aBffError({ response: { status: respuesta.status, data: cuerpo } })
        );
        return;
      }
      if (!respuesta.body) {
        handlers.onFailure?.(new BffError('network_error', 0));
        return;
      }

      const lector = respuesta.body.getReader();
      const decoder = new TextDecoder();
      const parser = new SseParser();

      const despachar = (evento: { event: string; data: string }) => {
        switch (evento.event) {
          case 'started': {
            const data = parseSseData<TStarted>(evento.data);
            if (data) handlers.onStarted?.(data);
            break;
          }
          case 'result': {
            const data = parseSseData<TResult>(evento.data);
            if (data) handlers.onResult?.(data);
            break;
          }
          case 'error': {
            const data = parseSseData<TError>(evento.data);
            if (data) handlers.onError?.(data);
            break;
          }
          case 'ping':
            handlers.onPing?.();
            break;
          case 'done': {
            const data = parseSseData<TDone>(evento.data);
            if (data) handlers.onDone?.(data);
            cancelar();
            break;
          }
          default:
            break;
        }
      };

      // eslint-disable-next-line no-constant-condition
      while (true) {
        const { done, value } = await lector.read();
        if (done) break;
        parser
          .push(decoder.decode(value, { stream: true }))
          .forEach(despachar);
        if (cerrado) break;
      }
      if (!cerrado) parser.flush().forEach(despachar);
    } catch (error) {
      if (abort.signal.aborted) return; // cancelación explícita: no es falla
      handlers.onFailure?.(aBffError(error));
    } finally {
      cerrado = true;
    }
  })();

  return { cancelar, terminado };
};
